import React, { useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import IconsColor from "util/iconsColor"
import { filterImportIssueRows, IAgencyImportIssueRow } from "./agencyImportUtil"
import { IMPORT_ISSUE_SECTIONS, ImportIssueSection } from "./importSummarySection"
import styles from "./importIssuesDrawer.module.scss"

type ImportIssuesDrawerProps = {
    section: ImportIssueSection
    count: number
    rows: IAgencyImportIssueRow[]
    onClose: () => void
}

/** Right-side panel listing every skipped row of one summary section. Mount only while open. */
const ImportIssuesDrawer: React.FC<ImportIssuesDrawerProps> = ({ section, count, rows, onClose }) => {

    const config = IMPORT_ISSUE_SECTIONS[section]
    const [search, setSearch] = useState("")
    const visibleRows = useMemo(() => filterImportIssueRows(rows, search), [rows, search])

    // Capture phase on window so Escape closes only the drawer, not the import popup underneath.
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== "Escape") return
            event.stopPropagation()
            onClose()
        }
        window.addEventListener("keydown", handleKeyDown, true)
        return () => window.removeEventListener("keydown", handleKeyDown, true)
    }, [onClose])

    return createPortal(
        <div
            className={styles.overlay}
            onClick={(event) => {
                if (event.target === event.currentTarget) onClose()
            }}
        >
            <aside className={styles.panel} role="dialog" aria-modal="true" aria-label={config.title}>
                <div className={styles.header}>
                    <div>
                        <h3 className={styles.title}>{config.title}</h3>
                        <p className={styles.description}>{config.drawerDescription(count)}</p>
                    </div>
                    <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close panel">
                        <IconsColor iconName="closeIcon" width="18" height="18" fillColor="none" strokeColor="currentColor" />
                    </button>
                </div>

                <div className={styles.searchBox}>
                    <IconsColor iconName="searchIcon" width="16" height="16" fillColor="none" strokeColor="#7a7a7a" />
                    <input
                        type="text"
                        className={styles.searchInput}
                        placeholder="Search by agency, office or agent..."
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        autoFocus
                    />
                </div>

                <div className={styles.list}>
                    {visibleRows.length === 0 ? (
                        <p className={styles.empty}>No records match your search.</p>
                    ) : (
                        visibleRows.map((row, index) => (
                            <div key={`${row.row}-${index}`} className={styles.card}>
                                <div className={styles.cardHeader}>
                                    <span className={styles.cardTitle}>{row.agency_name || "-"}</span>
                                    <span className={styles.cardRow}>Row {row.row || "-"}</span>
                                </div>
                                <p className={styles.cardLine}>
                                    <strong>Office:</strong> {row.office_name || "-"}
                                </p>
                                <p className={styles.cardLine}>
                                    <strong>Agent:</strong> {row.agent_name || "-"}
                                </p>
                                <p
                                    className={`${styles.cardLine} ${
                                        section === "duplicate" ? styles.detailWarning : styles.detailDanger
                                    }`}
                                >
                                    <strong>{config.detailLabel}:</strong> {row.detail}
                                </p>
                            </div>
                        ))
                    )}
                </div>

                <div className={styles.footer}>
                    {search.trim()
                        ? `Showing ${visibleRows.length} of ${rows.length} record(s)`
                        : `Showing ${rows.length} record(s) total`}
                </div>
            </aside>
        </div>,
        document.body,
    )
}

export default ImportIssuesDrawer
